import { Flex, Typography, message } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { dateTimeFormat } from "../../../utils/utils"
import { RoutesPaths } from "../../../constants/routes"
import { neutral } from "../tokens"
import { WidgetFrame } from "../WidgetFrame"
import type {
  ActivityFeedItem,
  FeedItem,
  WidgetProps,
  WidgetState,
} from "../types"
import { isActivityFeedItem } from "../types"

dayjs.extend(relativeTime)

const { Text } = Typography


// One glyph for every action card, as the reference has it — severity is
// carried by the tile's tint, not by a different silhouette per row.
const SeverityIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={22}
    height={22}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5M12 16.2h.01" />
  </svg>
)

const VISIBLE_LIMIT = 5
const SHOW_MORE_STEP = 25

/**
 * Next Best Actions owns the only warm surface on Home, so its palette is
 * spelled out here rather than taken from the cool widget tokens. The values
 * mirror `DashboardPage/home-theme`; this file sits in the shared widget
 * library and must not import a container's module.
 */
const NBA = {
  warmSection: "#FFFBF4",
  surface: "#FFFFFF",
  border: "#E1EAF1",
  amber: "#FF9800",
  ink: "#0B1533",
  inkMuted: "#63799E",
  // Same hue a step darker: #63799E is 4.41:1 on the card, just under AA.
  inkMutedStrong: "#5A7095",
  amberBg: "#FFF2DF",
  actionBg: "#CEF0F2",
  actionText: "#00647A",
  categoryBg: "#E5F0FF",
  categoryText: "#0062D6",
  shadow: "0 4px 18px rgba(20, 65, 85, 0.04)"
} as const

// Three compact cards across the desktop reference width (1280px+), two from
// the tablet breakpoint, one on a phone — the card never widens past the point
// where its own text stops filling it.
const NBA_GRID_CLASS =
  "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5 p-5 pt-3.5"
const NBA_CARD_GAP = 4
// The supporting line is the thing the button acts on, so it sits ~12px under
// it rather than being flushed to the card's bottom edge.
const NBA_ACTION_OFFSET = 12 - NBA_CARD_GAP
const NBA_RATIONALE_LINES = 3

const NBA_HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  padding: "14px 20px 0"
}

const NBA_TITLE_STYLE: React.CSSProperties = {
  fontSize: 20,
  lineHeight: "26px",
  fontWeight: 700,
  color: NBA.ink
}

const REVEAL_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  fontSize: 13,
  lineHeight: "18px",
  fontWeight: 600,
  color: NBA.actionText
}

/**
 * Every action tile is amber. Severity still travels on `data-severity` and
 * decides the order the server sends, but the tile is an attention marker,
 * not a status: red is Home's Delayed colour and would read as one more
 * failing project rather than "there is something to do here".
 */
const ACTION_TINT = { bg: NBA.amberBg, fg: NBA.amber } as const

const ArrowGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 12h15M13 6l6 6-6 6" />
  </svg>
)

const stringParam = (params: FeedItem["action"]["params"], key: string) => {
  const value = params[key]
  return typeof value === "string" && value ? value : undefined
}

const resolveActionRoute = (item: FeedItem): string | undefined => {
  const { kind, params } = item.action
  const url = stringParam(params, "url")
  if (url) return url

  const leadId = stringParam(params, "lead_id") ?? item.lead_id ?? undefined
  const projectId = stringParam(params, "project_id") ?? item.project_id ?? undefined

  if (kind === "send_reminder" && projectId) {
    return RoutesPaths.project.view(projectId).overview
  }
  if (leadId) return RoutesPaths.lead.view(leadId)
  if (projectId) return RoutesPaths.project.view(projectId).overview
  return undefined
}

interface NBASlotProps {
  items: FeedItem[]
}

const NBASlotImpl = ({ items }: NBASlotProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [visibleCount, setVisibleCount] = useState(VISIBLE_LIMIT)

  const handleAction = (item: FeedItem) => {
    const target = resolveActionRoute(item)
    if (target) {
      navigate(target)
      return
    }
    message.info(t("portfolio.nba.actionNotAvailable", "Action not available yet"))
  }

  if (!items.length) return null

  const visibleItems = items.slice(0, visibleCount)
  const hiddenCount = items.length - visibleItems.length
  const canCollapse = hiddenCount === 0 && items.length > VISIBLE_LIMIT

  const reveal = hiddenCount > 0 && (
    <button
      type="button"
      aria-expanded={false}
      onClick={() => setVisibleCount((prev) => Math.min(prev + SHOW_MORE_STEP, items.length))}
      style={REVEAL_STYLE}
    >
      {t("portfolio.nba.showMore", "Show more ({{count}})", { count: hiddenCount })}
      <ArrowGlyph />
    </button>
  )

  const collapse = canCollapse && (
    <button
      type="button"
      aria-expanded={true}
      onClick={() => setVisibleCount(VISIBLE_LIMIT)}
      style={REVEAL_STYLE}
    >
      {t("portfolio.nba.showLess", "Show less")}
    </button>
  )

  return (
    <section
      style={{
        background: NBA.warmSection,
        border: `1px solid ${NBA.border}`,
        borderRadius: 10,
        boxShadow: NBA.shadow,
        overflow: "hidden"
      }}
    >
      {/* Show more rides the heading rather than the section's bottom edge, so
          on a wide monitor it never ends up a screen away from what it opens. */}
      <div style={NBA_HEADER_STYLE}>
        <h2 style={{ ...NBA_TITLE_STYLE, margin: 0 }}>
          {t("portfolio.nba.title", "Next Best Actions")}
        </h2>
        {reveal}
        {collapse}
      </div>

      <div className={NBA_GRID_CLASS}>
        {visibleItems.map((item) => {
          return (
            <div
              key={item.id}
              data-testid={`nba-item-${item.id}`}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                minWidth: 0,
                padding: "16px 18px",
                borderRadius: 8,
                // The 4px amber rule is the section's signature; it reads as
                // one stripe with the icon tile rather than a second outline.
                borderLeft: `4px solid ${NBA.amber}`,
                border: `1px solid ${NBA.border}`,
                borderLeftWidth: 4,
                borderLeftColor: NBA.amber,
                background: NBA.surface
              }}
            >
              <div
                role="img"
                aria-label={t(`portfolio.nba.severity.${item.severity}`, item.severity)}
                data-testid="severity-icon"
                data-severity={item.severity}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: ACTION_TINT.bg,
                  color: ACTION_TINT.fg
                }}
              >
                <SeverityIcon />
              </div>

              {/* One text column: chip, title, detail, then the button —
                  nothing between them grows, so the button cannot drift. */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 4,
                  minWidth: 0
                }}
              >
                {item.action.category && (
                  <span
                    data-testid="nba-category"
                    data-category={item.action.category}
                    style={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 11,
                      lineHeight: "16px",
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      padding: "2px 8px",
                      borderRadius: 5,
                      background: NBA.categoryBg,
                      color: NBA.categoryText
                    }}
                  >
                    {t(`portfolio.nba.category.${item.action.category}`, item.action.category)}
                  </span>
                )}
                <Text
                  strong
                  style={{
                    fontSize: 15,
                    lineHeight: "21px",
                    fontWeight: 600,
                    color: NBA.ink
                  }}
                >
                  {item.title}
                </Text>
                <Text
                  title={item.rationale}
                  style={{
                    fontSize: 14,
                    lineHeight: "20px",
                    color: NBA.inkMutedStrong,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: NBA_RATIONALE_LINES,
                    overflow: "hidden"
                  } as React.CSSProperties}
                >
                  {item.rationale}
                </Text>
                <button
                  type="button"
                  onClick={() => handleAction(item)}
                  style={{
                    marginTop: NBA_ACTION_OFFSET,
                    height: 34,
                    padding: "0 16px",
                    fontSize: 13,
                    lineHeight: "18px",
                    fontWeight: 600,
                    color: NBA.actionText,
                    background: NBA.actionBg,
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  }}
                >
                  {t("portfolio.nba.action", "Take Action")}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

export const NBASlot = React.memo(NBASlotImpl)

const ActivityRow = ({ item }: { item: ActivityFeedItem }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const target = item.drill_to
  const absolute = dayjs(item.timestamp)
  const relative = absolute.isValid() ? absolute.fromNow() : item.timestamp
  const tooltip = absolute.isValid() ? absolute.format(dateTimeFormat) : undefined

  const handleOpen = () => {
    if (target) navigate(target)
  }

  return (
    <Flex
      data-testid={`feed-item-${item.id}`}
      role={target ? "button" : undefined}
      tabIndex={target ? 0 : undefined}
      onClick={target ? handleOpen : undefined}
      onKeyDown={
        target
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleOpen()
              }
            }
          : undefined
      }
      vertical
      gap={2}
      style={{
        padding: "10px 12px",
        border: `1px solid ${neutral.border}`,
        borderRadius: 10,
        background: neutral.surface,
        cursor: target ? "pointer" : "default",
      }}
    >
      <Text strong ellipsis={{ tooltip: item.title }}>
        {item.title}
      </Text>
      <Text
        type="secondary"
        data-testid="feed-timestamp"
        title={tooltip}
        aria-label={t("portfolio.feed.occurredAt", "Occurred at {{time}}", {
          time: tooltip ?? relative,
        })}
        style={{ fontSize: 12 }}
      >
        {relative}
      </Text>
    </Flex>
  )
}

const ActivityFeedImpl = ({
  widget,
  payload,
  state,
  onRefresh,
  onExpand
}: WidgetProps) => {
  const { t } = useTranslation()
  const items = (payload?.feed?.items ?? []).filter(isActivityFeedItem)
  const title = widget.config?.title ?? widget.title

  let effectiveState: WidgetState
  if (state === "loading" || state === "error") {
    effectiveState = state
  } else if (!payload?.feed) {
    effectiveState = "loading"
  } else if (items.length === 0) {
    effectiveState = "empty"
  } else {
    effectiveState = state ?? "ready"
  }

  return (
    <WidgetFrame
      title={title}
      state={effectiveState}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      payload={payload}
      emptyText={t("portfolio.feed.empty", "No recent activity")}
      onRefresh={onRefresh}
      onExpand={onExpand}
    >
      <Flex vertical gap={8}>
        {items.map((item) => (
          <ActivityRow key={item.id} item={item} />
        ))}
      </Flex>
    </WidgetFrame>
  )
}

export const ActivityFeed = React.memo(ActivityFeedImpl)

const EMPTY_ITEMS: FeedItem[] = []

export const FeedWidget = (props: WidgetProps) => {
  const items = props.payload?.feed?.items ?? EMPTY_ITEMS
  const { dataSource } = props.widget
  const isNbaFeed =
    dataSource === "nba" ||
    (dataSource == null && items.some((item) => !isActivityFeedItem(item)))
  if (isNbaFeed) {
    return <NBASlot items={items as FeedItem[]} />
  }
  return <ActivityFeed {...props} />
}
